import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
	addTaxPayer,
	startLoader,
} from '../../../redux/slices/taxProfile.slice';
import { RootState } from '../../../redux/store';
import { fetchTaxPayer } from '../../../services/taxes.service';
import LastPendingOrder from '../../pendingReferences/components/LastPendingOrder';
import { Slider } from '../../../pages/taxesAndInvoices/molecules/Slider';

import FinancialSummary from './FinancialSummary';
import { Prebilling } from '../../../components/organisms/Prebilling/Prebilling';
import FiscalProfileOverview from './FiscalProfileOverview';
import { DeclarationsOverview } from '../../../components/organisms/DeclarationsOverview/DeclarationsOverview';
import { ApprovalDeclaration } from '../../../@types/Declarations.type';
import { getUserDeclarationStatus } from '../../../services/flowApproval.service';
import { USERS_APPROVAL } from '../../../shared/constants/usersApproval';
import BannerServiceStatus from '../../../components/molecules/BannerServiceStatus';
import WidgetImss from './WidgetImss';
import { useService } from '../../../hooks/useService';

const UserCredentialsLinked: React.FC = () => {
	const { fetchLastDeclarationStatus } = useService();
	const [declarationsPending, setDeclarations] =
		useState<ApprovalDeclaration[]>();
	const [isLoadingApprovalDeclaration, setIsLoadingApprovalDeclaration] =
		useState(true);

	const taxPayer = useSelector((state: RootState) => state.taxProfile.taxPayer);
	const { user: userProfile, serviceStatus } = useSelector(
		(state: RootState) => state.profile,
	);

	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const userIsApproval = USERS_APPROVAL.includes(userProfile?.id || 0);

	const dispatch = useDispatch();

	const getTaxPayer = async () => {
		try {
			if (!userProfile?.id) return;
			dispatch(startLoader(true));
			const resource = await fetchTaxPayer(userProfile?.id, tokenSession);
			dispatch(addTaxPayer(resource));
		} catch (error: any) {
			dispatch(startLoader(false));
		}
	};

	const getFlowApproval = async () => {
		try {
			const response = await getUserDeclarationStatus();
			if (response.length > 0) {
				const sortedDeclarations = response.sort(
					(a: ApprovalDeclaration, b: ApprovalDeclaration) => {
						if (a.status === 'pending' && b.status !== 'pending') return -1;
						if (a.status !== 'pending' && b.status === 'pending') return 1;

						const yearDiff = Number(b.detail.year) - Number(a.detail.year);
						if (yearDiff !== 0) return yearDiff;

						return Number(b.detail.month) - Number(a.detail.month);
					},
				);

				setDeclarations(sortedDeclarations);
			}
			setIsLoadingApprovalDeclaration(false);
		} catch (error) {
			setIsLoadingApprovalDeclaration(false);
		}
	};

	useEffect(() => {
		getFlowApproval();
	}, []);

	useEffect(() => {
		if (taxPayer) return;
		getTaxPayer();
	}, [taxPayer, userProfile?.id]);

	useEffect(() => {
		if (!userProfile?.id) return;
		fetchLastDeclarationStatus(userProfile?.id);
	}, [userProfile?.id]);


	return (
		<div>
			<div className='w-full max-w-4xl mx-auto sm:px-6 flex flex-col gap-5 fade-in-scale'>
				<Prebilling />
				{serviceStatus && Object.keys(serviceStatus).length > 0 && serviceStatus.status !== 'completed' && <BannerServiceStatus />}
				<WidgetImss />
				<FiscalProfileOverview />
				<DeclarationsOverview />
				<FinancialSummary />
				<LastPendingOrder />
				{userIsApproval &&
					declarationsPending &&
					declarationsPending?.length > 0 &&
					!declarationsPending.every(
						(declaration) => declaration.fft_status === 'completed',
					) && (
						<Slider
							viewHome={true}
							declarationsPending={declarationsPending}
							isLoading={isLoadingApprovalDeclaration}
						/>
					)}
			</div>
		</div>
	);
};

export default React.memo(UserCredentialsLinked);
